import React, {useMemo} from 'react';
import {useSearchParams} from 'react-router-dom';
import {useIntl} from 'react-intl';
import {ReadableSizeHelper} from '../../Helpers/readableSize';
import '../../Styles/Hebergement/DetailsDomaine.css';
import {useGetMe} from '../../../../app/hooks/useGetMe';
import {useGetDomain} from '../../Hooks/DomainManager';

const DetailsDomaine = (): JSX.Element => {
  const intl = useIntl();
  const [searchParams] = useSearchParams();
  const ReadableSizeHelperInstance = useMemo(() => new ReadableSizeHelper(), []);
  const domaineName: string = searchParams.get('domaine') ?? '';
  const customer = useGetMe();
  const {data} = useGetDomain(customer?.id.toString() ?? '', domaineName);
  const yesNo = (value: boolean | undefined): string =>
    value
      ? intl.formatMessage({id: 'SERVICE.DOMAINE.INFOS.YES'})
      : intl.formatMessage({id: 'SERVICE.DOMAINE.INFOS.NO'});
  const convertDateFormat = (dateString: string): string => {
    const [datePart, timePart] = dateString.split(' ');
    const [year, month, day] = datePart.split('-');
    return `${day}/${month}/${year} ${timePart}`;
  };

  return (
    <>
      <div className="page-container">
        <div className="content-box">
          {/* Titre principal */}
          <h1 className="section-title">
            {intl.formatMessage({id: 'SERVICE.DOMAINE.INFOS.TITLE'})}{' '}
            {data != null ? data.domainName : ''}{' '}
          </h1>

          {/* Bloc principal */}
          <div className="info-block">
            <div className="info-row">
              <span className="label">
                {intl.formatMessage({id: 'SERVICE.DOMAINE.INFOS.FULL_NAME'})}
              </span>
              <span className="value">{data != null ? data.domainName : ''}</span>
            </div>

            <div className="info-row">
              <span>{intl.formatMessage({id: 'SERVICE.DOMAINE.INFOS.MANAGE_REGISTRY'})}</span>
              <span className={`badge ${data?.manage_registry ? 'blue' : 'gray'}`}>
                {yesNo(data?.manage_registry)}
              </span>
            </div>

            <div className="info-row">
              <span>{intl.formatMessage({id: 'SERVICE.DOMAINE.INFOS.MANAGE_DNS'})}</span>
              <span className={`badge ${data?.manage_dns ? 'blue' : 'gray'}`}>
                {yesNo(data?.manage_dns)}
              </span>
            </div>

            <div className="info-row">
              <span>{intl.formatMessage({id: 'SERVICE.DOMAINE.INFOS.MANAGE_MX'})}</span>
              <span className={`badge ${data?.manage_mx ? 'blue' : 'gray'}`}>
                {yesNo(data?.manage_mx)}
              </span>
            </div>

            <div className="info-row">
              <span>{intl.formatMessage({id: 'SERVICE.DOMAINE.INFOS.BACKUP_MX'})}</span>
              <span className={`badge ${data?.backup_mx ? 'blue' : 'gray'}`}>
                {yesNo(data?.backup_mx)}
              </span>
            </div>

            <div className="info-row">
              <span>{intl.formatMessage({id: 'SERVICE.DOMAINE.INFOS.MAX_FTP'})}</span>
              <span>
                {data?.nbFTPMax === 0
                  ? intl.formatMessage({id: 'SERVICE.DOMAINE.INFOS.UNLIMITED'})
                  : data?.nbFTPMax}
              </span>
            </div>

            <div className="info-row">
              <span>{intl.formatMessage({id: 'SERVICE.DOMAINE.INFOS.MAX_MAIL'})}</span>
              <span>
                {data?.nbMailMax === 0
                  ? intl.formatMessage({id: 'SERVICE.DOMAINE.INFOS.UNLIMITED'})
                  : data?.nbMailMax}
              </span>
            </div>

            <div className="info-row">
              <span>{intl.formatMessage({id: 'SERVICE.DOMAINE.INFOS.MAX_QUOTA'})}</span>
              <span>
                {data != null
                  ? data.quotaLimit === 0
                    ? intl.formatMessage({id: 'SERVICE.DOMAINE.INFOS.UNLIMITED'})
                    : ReadableSizeHelperInstance.readableSize(data.quotaLimit * 1000, 0, 'decimal')
                  : ''}
              </span>
            </div>
          </div>

          {/* Bloc WHOIS */}
          <h2 className="section-title">
            {intl.formatMessage({id: 'SERVICE.DOMAINE.INFOS.WHOIS_TITLE'})}
          </h2>

          <div className="info-block">
            <p className="note">{intl.formatMessage({id: 'SERVICE.DOMAINE.INFOS.WHOIS_NOTE'})}</p>

            <div className="info-row">
              <span className="label">
                {intl.formatMessage({id: 'SERVICE.DOMAINE.INFOS.WHOIS_STATUS'})}
              </span>
              <span>{data?.whois_status}</span>
            </div>

            <div className="info-row">
              <span>{intl.formatMessage({id: 'SERVICE.DOMAINE.INFOS.WHOIS_CREATION'})}</span>
              <span>{data?.whois_creation}</span>
            </div>

            <div className="info-row">
              <span>{intl.formatMessage({id: 'SERVICE.DOMAINE.INFOS.WHOIS_UPDATE'})}</span>
              <span>{data?.whois_update}</span>
            </div>

            <div className="info-row">
              <span>{intl.formatMessage({id: 'SERVICE.DOMAINE.INFOS.WHOIS_EXPIRATION'})}</span>
              <span>{data?.whois_expiration}</span>
            </div>

            <div className="info-row vertical">
              <span className="label">
                {intl.formatMessage({id: 'SERVICE.DOMAINE.INFOS.WHOIS_NS'})}
              </span>
              <ul className="dns-list">
                {data?.whois_ns != null
                  ? data?.whois_ns.split(' ').map((ns, index) => <li key={index}>{ns}</li>)
                  : ''}
              </ul>
            </div>

            <div className="info-row">
              <span>{intl.formatMessage({id: 'SERVICE.DOMAINE.INFOS.WHOIS_LASTCHECK'})}</span>
              <span>{data != null ? convertDateFormat(data.whois_lastcheck) : ''}</span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
export default DetailsDomaine;
